import './SlaveTabs.css';
import { getPinnedHeight } from './utils/getPinnedHeight';

import { TabPanel, TabView, TabViewTabChangeEvent } from 'primereact/tabview';
import {
  Dispatch,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router';
import { IonCard } from '@ionic/react';
import SlaveGrid from './components/SlaveGrid/SlaveGrid';
import DetailContent from '../DetailContent/DetailContent';
import {
  GlobalLocationStateType,
  GridRow,
  MatchMode,
} from '../../../types/types';
import Tooltip from '../Tooltip/Tooltip';
import useRoutes from '../../../hooks/useRoutes';
import { getSubsetAndItemParams } from '../../../utils/getSubsetAndItemParams';
import { useSelectedRowsContext } from '../../../hooks/context/useSelectedRowsContext';
import SelectedRowsCounter from '../SelectedRowsCounter/SelectedRowsCounter';
import { useDatasetContext } from '../../../hooks/context/useDatasetContext';
import { useSlaveDatasetContext } from '../../../hooks/context/useSlaveDatasetContext';
import { useSyncSubsetIdToTab } from './hooks/useSyncSubsetIdToTab';
import { useDetailDisabledTabFallback } from './hooks/useDetailDisabledTabFallback';
import { GridColumn } from '../Grid/types';
import { getVisibleSubsets } from './utils/getVisibleSubsets';

interface SlaveTabsProps {
  masterDataset: any;
  parentRow: GridRow;
  isDetail: boolean;
  tableScrollLeft?: number;
  detailColumns: any;
  slaveTabIndex: number;
  setSlaveTabIndex: (value: number) => void;
  slaveDatasets: any;
  setSlaveDatasets: (value: any) => void;
  setShowSlaveFilter?: (value: any) => void;
  slaveLazyStates: any;
  setSlaveLazyStates: (value: any) => void;
  slaveFilters: any;
  setSlaveFilters: (value: any) => void;
  referenceDatasets: any;
  setReferenceDatasets: Dispatch<any>;
  openFullscreenReference: (
    column: GridColumn,
    value: string | null,
    matchMode: MatchMode,
    label: string,
    datasetId: string,
    resolvedFilter?: Record<string, any> | null
  ) => void;
}

const SlaveTabs = ({
  masterDataset,
  parentRow,
  isDetail,
  tableScrollLeft = 0,
  detailColumns,
  slaveTabIndex,
  setSlaveTabIndex,
  slaveDatasets,
  setShowSlaveFilter,
  slaveLazyStates,
  setSlaveLazyStates,
  slaveFilters,
  referenceDatasets,
  setReferenceDatasets,
  openFullscreenReference,
}: SlaveTabsProps) => {
  const [pinnedHeight, setPinnedHeight] = useState<number | undefined>(
    undefined
  );

  // Whole slave card is the quick-filter shortcut scope so hovering anywhere in the subset (tab nav, header bar, grid) opens the active subset filter.
  const slaveCardRef = useRef<HTMLIonCardElement | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const routes = useRoutes();
  const history = useHistory();
  const { pathname } = useLocation<GlobalLocationStateType>();
  const { selectedSlaveRows, selectedSlaveVirtualRows } =
    useSelectedRowsContext();
  const { subsetId } = useDatasetContext();
  const { setSubsetId, setParentRow } = useSlaveDatasetContext();

  // Snapshot the current panel height so it can be held during the next load
  const snapshotPanelHeight = useCallback(() => {
    const h = panelRef.current?.offsetHeight;
    if (h && h > 0) setPinnedHeight(h);
  }, []);

  const handleChildLoadingChange = useCallback((loading: boolean) => {
    if (loading) {
      // Tab-switch path already pinned synchronously; this covers in-tab refresh
      setPinnedHeight((prev) =>
        getPinnedHeight(prev, panelRef.current?.offsetHeight)
      );
      return;
    }
    setPinnedHeight(undefined);
  }, []);

  const detailDisabled: boolean = masterDataset?.useDetail === false;
  const showDetailTab: boolean = !isDetail && !detailDisabled;
  const visibleSubsets = useMemo(
    () => getVisibleSubsets(masterDataset?.subsets, parentRow),
    [masterDataset?.subsets, parentRow]
  );
  const visibleMasterDataset = useMemo(
    () => ({ ...masterDataset, subsets: visibleSubsets }),
    [masterDataset, visibleSubsets]
  );

  // Sync active subsetId to context whenever the selected tab changes
  useSyncSubsetIdToTab(slaveTabIndex, visibleMasterDataset, setSubsetId);

  useDetailDisabledTabFallback({
    enabled: !isDetail && detailDisabled,
    datasetId: masterDataset?.datasetId,
    subsets: visibleSubsets,
    slaveTabIndex,
    setSlaveTabIndex,
  });

  useEffect(() => {
    setParentRow(parentRow);
  }, [parentRow]);

  const handleOpenSubset = (e: TabViewTabChangeEvent) => {
    // Slot 0 is the invisible placeholder when the Detail tab is off.
    if (detailDisabled && e.index === 0) return;

    snapshotPanelHeight();
    setSlaveTabIndex(e.index);

    if (isDetail) {
      // Hidden TabPanel at slot 0; subsets at 1+.
      if (e.index === 0) return;
      const newSubsetId = visibleSubsets[e.index - 1]?.datasetId;
      if (!newSubsetId) return;
      // Carry the router state (enteredFromExternalUrl) so a tab switch does
      // not drop the external single-record display mid-visit.
      history.push(
        routes.datasetDetailSubset(
          masterDataset.datasetId,
          parentRow[masterDataset.idItem] as string,
          newSubsetId
        ),
        history.location.state
      );
      return;
    }

    const newUrl =
      e.index === 0
        ? routes.subsetDetail(
            masterDataset.datasetId,
            parentRow[masterDataset.idItem] as string
          )
        : routes.subset(
            masterDataset.datasetId,
            parentRow[masterDataset.idItem] as string,
            visibleSubsets[e.index - 1].datasetId
          );
    history.push(newUrl);
  };

  // Re-sync on pathname change so browser back/forward between subsets
  // updates the active tab index. Without pathname dep, TabView stays on the
  // previous subset's panel while context loads the new dataset, and the
  // SlaveGrid guard (dataset.datasetId !== subsetId prop) holds a spinner.
  useEffect(() => {
    const { itemId: itemIdParam, subsetId: subsetIdParam } =
      getSubsetAndItemParams(pathname);

    if (subsetIdParam) {
      const subsetIndex = visibleSubsets.findIndex(
        (subset) => subset.datasetId === subsetIdParam
      );
      // Subsets occupy slots 1+ in both modes (slot 0 = Detail tab on list, hidden on detail).
      const resolvedIndex = subsetIndex + 1;

      if (subsetIndex !== -1 && resolvedIndex !== slaveTabIndex) {
        snapshotPanelHeight();
        setSlaveTabIndex(resolvedIndex);
        return;
      }
      if (subsetIndex !== -1) return;

      const firstSubsetId = visibleSubsets[0]?.datasetId;
      const itemValue = parentRow?.[masterDataset.idItem];
      if (firstSubsetId && itemValue !== undefined) {
        const fallbackUrl = isDetail
          ? routes.datasetDetailSubset(
              masterDataset.datasetId,
              itemValue as string,
              firstSubsetId
            )
          : routes.subset(
              masterDataset.datasetId,
              itemValue as string,
              firstSubsetId
            );
        history.replace(fallbackUrl, history.location.state);
      }
      return;
    }

    // List mode with row expanded but no subset segment -> Detail tab (slot 0)
    if (showDetailTab && itemIdParam !== undefined && slaveTabIndex !== 0) {
      snapshotPanelHeight();
      setSlaveTabIndex(0);
    }

    // Detail mode without subset segment -> push URL of the auto-selected
    // first subset so the URL reflects the visible tab.
    if (isDetail && !subsetIdParam) {
      const firstSubsetId = visibleSubsets[0]?.datasetId;
      const itemValue = parentRow?.[masterDataset.idItem];
      if (firstSubsetId && itemValue !== undefined) {
        history.replace(
          routes.datasetDetailSubset(
            masterDataset.datasetId,
            itemValue as string,
            firstSubsetId
          ),
          history.location.state
        );
      }
    }
  }, [pathname, visibleSubsets, parentRow, isDetail, showDetailTab]);

  const containerStyle = {
    marginLeft: `${tableScrollLeft}px`,
    ...(pinnedHeight !== undefined && { minHeight: `${pinnedHeight}px` }),
  };

  return (
    <div className="slave-container" style={containerStyle} ref={panelRef}>
      <IonCard className="slave-card" ref={slaveCardRef}>
        <Tooltip target=".tab-header" mouseTrack />
        <TabView activeIndex={slaveTabIndex} onTabChange={handleOpenSubset}>
          {showDetailTab ? (
            <TabPanel header="Detail" key="detail">
              <DetailContent
                row={parentRow}
                dataset={masterDataset}
                detailColumns={detailColumns}
                onLoadingChange={handleChildLoadingChange}
              />
            </TabPanel>
          ) : (
            <TabPanel visible={false}></TabPanel>
          )}
          {visibleSubsets.map((subset) => (
            <TabPanel
              header={
                <span
                  className="tab-header"
                  data-pr-tooltip={subset.hint}
                  data-pr-position="bottom"
                >
                  {subset.label}
                  {subset.datasetId === subsetId && (
                    <SelectedRowsCounter
                      selectedRows={selectedSlaveRows}
                      selectedVirtualRows={selectedSlaveVirtualRows}
                    />
                  )}
                </span>
              }
              key={subset.datasetId}
            >
              <SlaveGrid
                subsetId={subset.datasetId}
                slaveDatasets={slaveDatasets}
                setShowSlaveFilter={setShowSlaveFilter}
                slaveLazyState={slaveLazyStates[subset.datasetId]}
                setSlaveLazyStates={setSlaveLazyStates}
                slaveFilter={slaveFilters[subset.datasetId]}
                referenceDatasets={referenceDatasets}
                setReferenceDatasets={setReferenceDatasets}
                openFullscreenReference={openFullscreenReference}
                onLoadingChange={handleChildLoadingChange}
                shortcutScopeRef={slaveCardRef}
              />
            </TabPanel>
          ))}
        </TabView>
      </IonCard>
    </div>
  );
};

export default SlaveTabs;
